/** Loyalty earn/redeem math — shared between dashboard preview and POS checkout. */
/** Fixed product vocabulary — not cafe-configurable. Edge copy: supabase/functions/_shared/loyalty.ts */
export const LOYALTY_POINT_NAME = 'Points';
/** Wallet pass secondary field title. */
export const LOYALTY_PASS_CARD_TITLE = 'Loyalty Rewards';
const LOYALTY_MEMBER_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** True when input is a loyalty member row id (encoded in wallet QR). */
export function isLoyaltyMemberId(value) {
    return LOYALTY_MEMBER_ID_RE.test(value.trim());
}
export function parseCategoryDiscounts(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw))
        return {};
    const out = {};
    for (const [key, val] of Object.entries(raw)) {
        if (!key)
            continue;
        const n = Number(val);
        if (!Number.isFinite(n) || n <= 0)
            continue;
        out[key] = Math.min(100, Math.max(0, n));
    }
    return out;
}
/** Highest configured category rate (for wallet pass / summaries). */
export function maxCategoryDiscountPercent(map, fallback = 0) {
    const vals = Object.values(map);
    if (vals.length === 0)
        return Math.max(0, fallback);
    return Math.max(0, fallback, ...vals);
}
export function sanitizeCategoryDiscounts(map) {
    return parseCategoryDiscounts(map);
}
function calcPercentDiscountAmd(subtotal, discountPercent, lines, categoryDiscounts) {
    const map = categoryDiscounts ?? {};
    const hasCategoryRates = Object.keys(map).length > 0;
    if (hasCategoryRates && lines && lines.length > 0) {
        const discount = lines.reduce((sum, line) => {
            const pct = map[line.categoryId] ?? discountPercent;
            return sum + Math.round((Math.max(0, line.lineTotal) * Math.max(0, pct)) / 100);
        }, 0);
        return Math.min(subtotal, discount);
    }
    return Math.min(subtotal, Math.round((subtotal * Math.max(0, discountPercent)) / 100));
}
/** Default redeem rate: 10× worse for the customer than earn. */
export function defaultRedeemAmdPerCoin(earnAmdPerCoin) {
    return Math.max(0.01, earnAmdPerCoin / 10);
}
export function calcLoyaltyCheckout(input) {
    const subtotal = Math.max(0, input.subtotal);
    const percentDiscountAmd = calcPercentDiscountAmd(subtotal, input.discountPercent, input.lines, input.categoryDiscounts);
    const afterPercent = Math.max(0, subtotal - percentDiscountAmd);
    const wantRedeem = input.redeemCoins !== false;
    const maxCoinDiscountAmd = wantRedeem
        ? Math.floor(Math.max(0, input.coinBalance) * input.redeemAmdPerCoin)
        : 0;
    const coinDiscountAmd = Math.min(afterPercent, maxCoinDiscountAmd);
    let coinsRedeemed = 0;
    if (coinDiscountAmd > 0 && input.redeemAmdPerCoin > 0) {
        coinsRedeemed = Math.min(input.coinBalance, Math.floor(coinDiscountAmd / input.redeemAmdPerCoin));
    }
    const totalDiscountAmd = percentDiscountAmd + coinDiscountAmd;
    const payableTotal = Math.max(0, subtotal - totalDiscountAmd);
    const coinsEarnedPreview = input.earnAmdPerCoin > 0 ? Math.floor(payableTotal / input.earnAmdPerCoin) : 0;
    return {
        subtotal,
        percentDiscountAmd,
        coinDiscountAmd,
        totalDiscountAmd,
        coinsRedeemed,
        payableTotal,
        coinsEarnedPreview,
    };
}
/** Generate a unique 10-digit member code for QR / manual entry. */
export function generateMemberCode() {
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
        const arr = new Uint32Array(2);
        crypto.getRandomValues(arr);
        const n = (arr[0] % 9000000000) + 1000000000;
        return String(n);
    }
    const n = Math.floor(Math.random() * 9000000000) + 1000000000;
    return String(n);
}
/** Minimum custom customer number when link allows signups to choose their own. */
export const LOYALTY_CUSTOM_MEMBER_CODE_MIN = 1000;
export function normalizeMemberCodeInput(raw) {
    return raw.replace(/\D/g, '');
}
/** Returns an error message, or null when valid. */
export function validateCustomMemberCode(raw) {
    const normalized = normalizeMemberCodeInput(raw);
    if (!normalized)
        return 'Enter a customer number.';
    if (!/^\d+$/.test(normalized))
        return 'Customer number must contain digits only.';
    const n = Number(normalized);
    if (!Number.isSafeInteger(n) || n < LOYALTY_CUSTOM_MEMBER_CODE_MIN) {
        return `Customer number must be at least ${LOYALTY_CUSTOM_MEMBER_CODE_MIN}.`;
    }
    if (normalized.length > 10)
        return 'Customer number is too long.';
    return null;
}
/** Slug for join link URLs — lowercase alphanumeric + hyphens. */
export function slugifyJoinLinkName(name) {
    return (name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 48) || 'link');
}
//# sourceMappingURL=loyalty.js.map